import { useBuy } from "@/context/buy";
import { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import { motion } from "framer-motion";
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from "@/firebase";
import { encryptInteger } from "@/Utils/encryptInteger";
import { useLoading } from "@/context/loading";
import { useRouter } from "next/router";

export default function CreateUserModal({ setCreateNewUserModalOpen }) {
  const router = useRouter();
  const { googleToken, setLoggedUserDetails } = useBuy();
  const { startLoading, stopLoading } = useLoading();
  const [data, setData] = useState();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteService = useRef(null);
  const sessionToken = useRef(null);
  const inputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      phone_number: "+1",
      country_code: "+1",
      agree: false,
      is_sms_on: false,
    },

    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      phone_number: Yup.string().required("Phone Number is required"),
      agree: Yup.boolean().oneOf([true], "You must agree to the terms"),
    }),

    onSubmit: async (values) => {
      try {
        startLoading("Creating your profile...");
        setIsSubmitting(true);
        const fullPhone = `+${values.phone_number}`;

        if (!window.recaptchaVerifier) {
          window.recaptchaVerifier = new RecaptchaVerifier(
            auth,
            "submit-button-id",
            {
              size: "invisible",
              callback: () => { },
              "expired-callback": () => {
                toast.error("reCAPTCHA expired. Please try again.");
                stopLoading();
              },
            }
          );
          await window.recaptchaVerifier.render();
        }

        const formData = new FormData();
        formData.append("action", "addProfile");
        formData.append("token", googleToken);
        formData.append("name", values.name);
        formData.append("phone_number", fullPhone);
        formData.append("city", 'Denver');
        formData.append("state", 'CO');
        formData.append("country_code", values.country_code);
        formData.append("agree", values.agree);
        formData.append("is_sms_on", values.is_sms_on ? 1 : 0);

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUser`,
          {
            method: "POST",
            body: formData,
            headers: {
              Authorization: `Bearer ${googleToken}`,
            },
          }
        );

        const data = await response.json();

        if (data.status) {
          toast.success("Profile added successfully!");
          const storingData = {
            profile: data?.profile,
            web_token_expires_at: data?.web_token_expires_at,
            web_token: data?.web_token,
            name: values?.name,
            state: values?.state,
            user_type: data?.data?.user_type,
          };
          const encryptedData = encryptInteger(JSON.stringify(storingData));
          localStorage.setItem("user_data", encryptedData);
          setLoggedUserDetails({
            status: true,
            data: storingData,
            remove: false,
          });
          router.push("/");
        } else {
          toast.error(data.message || "Failed to add profile");
          stopLoading();
        }
      } catch (error) {
        console.error("API Error:", error.message);
        toast.error("Something went wrong. Please try again.");
        stopLoading();
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    getStatesData();

    const initializePlaces = async () => {
      try {
        const { AutocompleteSuggestion, AutocompleteSessionToken } =
          await google.maps.importLibrary("places");
        autocompleteService.current = AutocompleteSuggestion;
        sessionToken.current = new AutocompleteSessionToken();
      } catch (error) {
        console.error("Error initializing Places library:", error);
        toast.error("Failed to initialize Places service");
      }
    };

    if (window.google?.maps) {
      initializePlaces();
    }
  }, []);

  const handleInputChange = async (value) => {
    formik.setFieldValue("city_data", value);

    if (!value || value.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (!autocompleteService.current) {
      console.log("Google Maps not loaded yet, waiting...");
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const request = {
        input: value,
        includedRegionCodes: ["us"],
        includedPrimaryTypes: ["locality"],
        sessionToken: sessionToken.current,
        language: "en",
      };

      const response =
        await autocompleteService.current.fetchAutocompleteSuggestions(request);

      if (response && response.suggestions) {
        setSuggestions(response.suggestions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = async (index) => {
    try {
      const suggestion = suggestions[index];

      if (suggestion && suggestion.placePrediction) {
        const mainText =
          suggestion.placePrediction.text?.text ||
          suggestion.placePrediction.mainText?.text ||
          "";

        const parts = mainText.split(", ");
        if (parts.length >= 2) {
          const city = parts[0].trim();
          const statePart = parts[1].trim();

          const state = statePart.split(" ")[0];

          const formattedAddress = `${city}, ${state}`;
          formik.setFieldValue("city_data", formattedAddress);
          formik.setFieldValue("state", state);
          formik.setFieldValue("city", city);

          const { AutocompleteSessionToken } = await google.maps.importLibrary(
            "places"
          );
          sessionToken.current = new AutocompleteSessionToken();
        } else {
          formik.setFieldValue("city_data", mainText);
          formik.setFieldValue("city", mainText);
        }
      } else {
        console.error("Invalid suggestion structure:", suggestion);
        toast.error("Invalid location data received");
      }
    } catch (error) {
      console.error("Error getting place details:", error);
      toast.error("Error getting location details");
    }

    setShowSuggestions(false);
  };

  const getStatesData = async () => {
    try {
      const formData = new FormData();
      formData.append("action", "getAllState");

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageState`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      setData(data.data);
    } catch (error) {
      console.error("API Error:", error.message);
    }
  };

  return (
    <div
      id="CreateUserDetailsModal"
      className="bg-gray-800 relative z-[999999] font-urbanist"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="flex flex-col md:flex-row justify-start max-h-screen overflow-hidden lg:items-center lg:justify-center fixed  h-full inset-0 px-7 md:px-0 bg-gray-700/50 z-[999]"
        onClick={() => setCreateNewUserModalOpen(false)}
      >
        <div className="md:hidden" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-end  mt-3 -mr-5">
            <div
              onClick={() => setCreateNewUserModalOpen(false)}
              className=" w-10 h-10 rounded-full bg-[#1F222A] flex  items-center justify-center mb-2 "
            >
              <svg
                width="13"
                height="12"
                viewBox="0 0 13 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.144 12L5.496 5.352V6.552L0.456 0.287999H3.288L6.864 4.848H6L9.576 0.287999H12.408L7.368 6.504V5.424L12.72 12H9.84L6 7.152H6.816L3 12H0.144Z"
                  fill="#6A798F"
                />
              </svg>
            </div>
          </div>
        </div>
        <div
          id="login-modal"
          className="bg-[#17191d] relative z-[999999999] h-fit w-full max-w-[620px] md:my-[20px] overflow-y-auto px-[20px] pb-[40px] rounded-[16px] lg:rounded-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mt-6 lg:mt-0 md:px-8">
            <div className="lg:mt-11 createeAcc text-xl">
              <p>Create Account</p>
            </div>
            <form action="" className="mt-5 pl-2 lg:pl-0 lg:mt-7">
              <div className="">
                <div className="form-item mt-4 flex flex-col gap-2 ">
                  <label
                    className="text-[10px] lg:text-base text-[#718096]"
                    htmlFor="last_name"
                  >
                    Name
                  </label>
                  <div className="relative">
                    <input
                      className="bg-[#E2E8F0] w-full text-[#2D3748] focus:outline-none text-[12px] lg:text-base rounded-[5px] p-3 lg:p-3 "
                      placeholder="Enter your Name"
                      type="text"
                      name="name"
                      {...formik.getFieldProps("name")}
                      required
                    />
                    {formik.touched.name && formik.errors.name ? (
                      <div className="text-red-500 text-sm">
                        {formik.errors.name}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="form-item mt-4 flex flex-col gap-2 ">
                  <label
                    className="text-[10px] lg:text-base text-[#718096]"
                    htmlFor="phone_number"
                  >
                    Phone number
                  </label>
                  <input
                    type="hidden"
                    name="country_code"
                    value={formik.country_code}
                  />
                  <div className="flex w-full gap-2 items-start ">
                    <div className="w-[48px] h-[48px] ">
                      <PhoneInput
                        country={"us"}
                        value={formik.values.country_code}
                        onChange={(value, country) => {
                          formik.setFieldValue("country_code", `+${country.dialCode}`);
                          formik.setFieldValue("phone_number", `+${country.dialCode}`);
                        }}
                        inputStyle={{
                          display: "none",
                        }}
                        buttonStyle={{
                          borderRadius: "6px",
                          height: "40px",
                          border: "1px solid #ccc",
                        }}
                        dropdownStyle={{
                          zIndex: 9999,
                        }}
                        disableCountryCode={true}
                        disableDropdown={false}
                        enableAreaCodes={false}
                        className="min-w-[38px] detailsCustomInput "
                      />
                    </div>

                    <input
                      type="text"
                      name="phone_number"
                      placeholder="123-456-7890"
                      className="bg-[#36383F] w-full text-[#fff] focus:outline-none text-[12px] lg:text-base rounded-[5px] p-3 lg:p-3 "
                      value={formik.values.phone_number}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^0-9\-]/g, "");
                        formik.setFieldValue("phone_number", `+${cleaned}`);
                      }} />
                  </div>

                  {formik.touched.phone_number && formik.errors.phone_number && (
                    <div className="text-red-500 text-sm">{formik.errors.phone_number}</div>
                  )}
                </div>
                <div className="mt-6 lg:mt-[50px] flex items-start lg:items-start gap-2">
                  <input
                    type="checkbox"
                    className="custom_checkbox min-w-[24px] mt-2 lg:mt-1"
                    id="agree"
                    checked={formik.values.is_sms_on}
                    onChange={() =>
                      formik.setFieldValue(
                        "is_sms_on",
                        !formik.values.is_sms_on
                      )
                    }
                  />
                  <p className="font-urbanist text-sm lg:text-base text-[#9E9E9E]">
                    <span>I </span>
                    <span className="text-white font-[700] mb-0">
                      consent to receive SMS
                    </span>
                    <span>
                      {" "}
                      reminders and important event updates about this ticket
                      purchase.
                    </span>
                  </p>
                </div>
                <div className="mt-3 lg:mt-[50px] flex items-start lg:items-center gap-2">
                  <input
                    type="checkbox"
                    className="custom_checkbox mt-2 lg:mt-0"
                    id="agree"
                    checked={formik.values.agree}
                    onChange={() =>
                      formik.setFieldValue("agree", !formik.values.agree)
                    }
                  />
                  <p className="font-urbanist text-xs lg:text-base text-[#9E9E9E]">
                    I agree to the{" "}
                    <a
                      href="https://help.qoncertapp.com/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-[700] text-white hover:text-[#fa2cb4]"
                    >
                      Privacy
                    </a>
                    ,{" "}
                    <a
                      href="https://help.qoncertapp.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-[700] text-white hover:text-[#fa2cb4]"
                    >
                      Terms
                    </a>{" "}
                    <span className="text-white ">and </span>
                    <a
                      href="https://help.qoncertapp.com/purchase-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-[700] text-white hover:text-[#fa2cb4]"
                    >
                      Purchase Terms
                    </a>
                  </p>
                </div>

                {formik.touched.agree && formik.errors.agree ? (
                  <div className="text-red-500 text-sm">
                    {formik.errors.agree}
                  </div>
                ) : null}
              </div>
            </form>
            <div className="mt-5 lg:mt-12 pt-1 relative">
              <div className="absolute w-[120%] left-[-10%]">
                <svg
                  width="100%"
                  height="2"
                  viewBox="0 0 694 2"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line y1="1" x2="694.001" y2="1" stroke="#332D41" />
                </svg>
              </div>
            </div>
            <div
              className={`mt-5 lg:mt-5 modal-footer flex  gap-7 items-center`}
            >
              <button
                onClick={() => {
                  setCreateNewUserModalOpen(false);
                }}
                className={`border-[#35383F] hidden lg:block bg-[#6b6b6b17] w-full font-urbanist font-[500] border text-[14px] lg:text-base py-[6px] lg:py-[19px] lg:leading-[10px] px-[62px]  rounded-[10px]  `}
              >
                {" "}
                Close
              </button>
              <button
                id="submit-button-id"
                onClick={() => {
                  if (!isSubmitting) {
                    formik.handleSubmit();
                  }
                }}
                disabled={isSubmitting}
                className={`main_btn_bg font-[800] text-[14px] font-urbanist lg:text-base ${isSubmitting
                    ? "py-[8px] lg:py-[16px] "
                    : " py-[10px] lg:py-[20px]"
                  } lg:leading-[10px] px-[62px] w-[179px] lg:w-[340px] rounded-[10px] flex items-center justify-center gap-2 disabled:opacity-50`}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                      ></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  "Continue"
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
